import { compact, findKey, isEmpty } from 'lodash-es';
import {
  BetslipTicketType,
  BetslipType,
  BetslipWithBetsExtendedInfo,
  BettingSlipAcceptStatus,
  BettingType,
  BetWithMarketOutcomeEvent,
  GlobalTicketConditionsResponse,
  Market,
  MarketType,
  Outcome,
  OutcomeType,
  PublishedEventParticipants,
  SpecialValue,
  StakeErrors,
  StakeErrorType,
  UserBalance,
} from '../@types';
import {
  BETSLIP_TICKET_TYPE,
  BETTING_TYPE_HIT_COUNT_MAP,
  getLocalizedBettingTypeMap,
  MARKET_TYPE_ASIAN_HANDICAP_WITH_SCORE,
} from '../constants';
import type { BetslipEvent } from '../types/betslip';
import { isTruthy } from '../utils';
import { calculateMaxStakeAmountForNormalBettingSlip } from 'sportsbook-shared-module';
import { TFunction } from 'i18next';
import { formatOdds } from '../utils/oddsConverter';
import { OddsFormat, WalletType } from '../types/enums';

export const isValidNumericString = (inputString: string): boolean => {
  const numericPattern = /^(0|[1-9]\d*)$|^0\.\d{0,2}$|^[1-9]\d*\.\d{0,2}$/;

  return numericPattern.test(inputString) || inputString === '';
};

export const isDev = (): boolean => import.meta.env.DEV;

export const isJSON = (str: string) => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }

  return true;
};

export const getSystemBettingType = (requiredHitCount: number, bettingSlipType: BetslipType, t: TFunction) => {
  if (bettingSlipType === 'Single') return;
  const bettingType = findKey(BETTING_TYPE_HIT_COUNT_MAP, (item) => item === requiredHitCount);
  return getLocalizedBettingType(t, bettingType as BettingType);
};

export const getStake = (oddsFormat: OddsFormat, bet?: BetslipWithBetsExtendedInfo) => {
  if (!bet) return null;

  return compact([
    parseNumberWithDecimals(bet.stakeAmountPerCombination, 2),
    ' @ ',
    bet.totalOdds && `${formatOdds(bet.totalOdds ?? 0, oddsFormat)} `,
  ]).join('');
};

export const getResolveStatus = (tab: string) => {
  if (tab === 'all') return undefined;
  if (tab === 'others') return ['void']; // There's no others status on BE, it's been decided to use void instead until implementation
  if (tab === 'cancelled') return ['cancelled'];
  if (tab === 'refunded') return ['void'];
  return [tab];
};

export const isNumberString = (value: string) => !isNaN(Number(value));
export const parseNumberWithDecimals = (value: string | number | null, decimals: number) =>
  Number(value).toFixed(decimals);

export const hasUpdateStakesAction = (stakeErrors: StakeErrors) =>
  !isEmpty(stakeErrors) && Object.values(stakeErrors).some((stakeError) => stakeError.errorType !== 'maxWinLimit');

const hasMoreThanTwoDecimals = (num: number): boolean => {
  const parts = String(num).split('.');
  return parts.length > 1 && parts[1].length > 2;
};

// One formatter per locale: formatValue runs on every animation frame of the jackpot count-ups.
const currencyAmountFormatters = new Map<string, Intl.NumberFormat>();

const getCurrencyAmountFormatter = (locale: string) => {
  let formatter = currencyAmountFormatters.get(locale);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    currencyAmountFormatters.set(locale, formatter);
  }

  return formatter;
};

export const formatValue = (amount: number | null | undefined, locale: string) => {
  const numericBalance = typeof amount === 'number' ? amount : 0;
  if (!Number.isFinite(numericBalance)) return '0.00';

  const flooredBalance = hasMoreThanTwoDecimals(numericBalance)
    ? Math.floor(numericBalance * 100) / 100
    : numericBalance;

  try {
    return getCurrencyAmountFormatter(locale).format(flooredBalance);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to format number for locale: ${locale}`, error);
    return flooredBalance.toFixed(2);
  }
};

export const getEventPartScoreColor = (eventPartName: string) => {
  if (eventPartName?.toLowerCase() === 'whole match') {
    return 'secondary.main';
  } else if (eventPartName?.toLocaleLowerCase().includes('half')) {
    return 'neutral.300';
  } else {
    return 'neutral.50';
  }
};

export const getBettingSlipSnackbarMessage = (status: BettingSlipAcceptStatus, count: number, t: TFunction) => {
  switch (status) {
    case 'pending':
      return ` ${t('ticketsRequireConfirmation', { count })}`;
    case 'rejected':
      return ` ${t('ticketsRejected', { count })}`;
    case 'accepted':
      return `${t('ticketsAccepted', { count })}`;
    default:
      return '';
  }
};

export const getBetTypeLabel = (requiredHitCount: number) => {
  if (requiredHitCount === 1) {
    return 'Single';
  } else if (requiredHitCount === 2) {
    return 'Double';
  } else if (requiredHitCount === 3) {
    return 'Treble';
  }
  return `${requiredHitCount} Fold`;
};

export const getBalanceAmount = (balances: UserBalance[] | undefined, type: string) => {
  if (!balances) return;
  return balances?.find((balance) => balance.type === type)?.balance;
};

export const getBalanceData = (balances: UserBalance[] | undefined, type: string) => {
  if (!balances) return;
  return balances?.find((balance) => balance.type === type);
};

export const getOutcomeName = (outcome: Outcome, isMarketSingleDisplayType: boolean) => {
  if (isMarketSingleDisplayType) {
    return outcome.shortName || outcome.name;
  }

  return outcome.outcomeType?.shortName || outcome.outcomeType?.name || '';
};

export const sortMarketsBySpecialValues = (a: Market, b: Market) => {
  const modelIdA = a.specialValues?.[0].modelId || '';
  const modelIdB = b.specialValues?.[0].modelId || '';

  if (modelIdA !== modelIdB) {
    return modelIdA.localeCompare(modelIdB);
  }

  return (a.specialValues?.[0].value || 0) - (b.specialValues?.[0].value || 0);
};

const isNumeric = (value: string) => {
  const number = parseFloat(value);
  return !isNaN(number) && isFinite(number);
};

export const extractSpecialValuesFromOutcomeName = (
  outcomeName: string,
  specialValues: SpecialValue[] = [],
  isHandicapMarket: boolean,
  marketTypeName?: string
) => {
  if (marketTypeName === MARKET_TYPE_ASIAN_HANDICAP_WITH_SCORE) {
    const specialValue = outcomeName.split(' ').pop();

    if (specialValue && isNumeric(specialValue)) {
      return [specialValue];
    }
  }

  // For non-handicap markets, extract only numeric values
  const regex = isHandicapMarket ? /\(([^()]+(?:\([^()]*\)[^()]*)*)\)/g : /\(([-+]?[0-9]*\.?[0-9]+)\)/g;
  const specialValuesFinal: string[] = [];
  let match;

  while ((match = regex.exec(outcomeName)) !== null) {
    const outcomeNameBracketsValues = match[1].split(' ');
    const SVValues = specialValues.map((sv) => Math.abs(sv.value || 0));

    outcomeNameBracketsValues.forEach((value) => {
      if (isNumeric(value)) {
        const absoluteValue = Math.abs(parseFloat(value));
        if (SVValues.includes(absoluteValue)) {
          specialValuesFinal.push(value);
        }
      } else {
        if ((value !== '(W)' && value !== 'W') || !isEmpty(specialValuesFinal)) {
          specialValuesFinal.push(value);
        }
      }
    });
  }

  return specialValuesFinal;
};

export const getOutcomeCellWidth = (outcomesLength: number) => {
  if (outcomesLength > 4) {
    return '33.33%';
  }

  return `calc(100% / ${outcomesLength})`;
};

export const analyzeBetslip = (
  bets: BetslipEvent[]
): { preMatchCount: number; inPlayCount: number; betCount: number; betslipTicketType: BetslipTicketType } => {
  let preMatchCount = 0;
  let inPlayCount = 0;

  for (const bet of bets) {
    if (bet.isLive) {
      inPlayCount++;
    } else {
      preMatchCount++;
    }
  }

  let betslipTicketType: BetslipTicketType = BETSLIP_TICKET_TYPE.IN_PLAY;

  if (inPlayCount && preMatchCount) {
    betslipTicketType = BETSLIP_TICKET_TYPE.MIXED;
  } else if (inPlayCount === 0) {
    betslipTicketType = BETSLIP_TICKET_TYPE.PRE_MATCH;
  }

  return { preMatchCount, inPlayCount, betCount: preMatchCount + inPlayCount, betslipTicketType };
};

export const calculateRoundedMinStakeAmount = (combinations: number, minStake: number) => {
  let rawStakeAmount = minStake / combinations;
  let roundedStakeAmount = +rawStakeAmount.toFixed(2);
  let minTicketStake = combinations * roundedStakeAmount;
  while (minTicketStake < minStake) {
    rawStakeAmount += 0.01;
    roundedStakeAmount = +rawStakeAmount.toFixed(2);
    minTicketStake = combinations * roundedStakeAmount;
  }
  return roundedStakeAmount;
};

export const mapStakeToCondition = (
  ticketType: BetslipTicketType,
  errorType: StakeErrorType,
  walletType: WalletType,
  conditions?: GlobalTicketConditionsResponse,
  combinationsNumber = 1
) => {
  let stakeLimit: number | null | undefined;
  let minStake: number | undefined;
  let maxStake: number | undefined;
  switch (errorType) {
    case 'min':
      minStake = conditions?.[walletType]?.[ticketType].minTicketStake ?? 0;
      stakeLimit = calculateRoundedMinStakeAmount(combinationsNumber, minStake);
      break;
    case 'minSingleInPlay':
      minStake = conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.IN_PLAY]?.minTicketStake ?? 0;
      stakeLimit = calculateRoundedMinStakeAmount(combinationsNumber, minStake);
      break;
    case 'minSinglePreMatch':
      minStake = conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.PRE_MATCH]?.minTicketStake ?? 0;
      stakeLimit = calculateRoundedMinStakeAmount(combinationsNumber, minStake);
      break;
    case 'max':
      maxStake = conditions?.[walletType]?.[ticketType].maxTicketStake ?? Infinity;
      stakeLimit = calculateMaxStakeAmountForNormalBettingSlip(combinationsNumber, maxStake);
      break;
    case 'maxSingleInPlay':
      maxStake =
        conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.IN_PLAY]?.maxStakeSingle ??
        conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.IN_PLAY]?.maxTicketStake ??
        Infinity;
      stakeLimit = calculateMaxStakeAmountForNormalBettingSlip(combinationsNumber, maxStake);
      break;
    case 'maxSinglePreMatch':
      maxStake =
        conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.PRE_MATCH]?.maxStakeSingle ??
        conditions?.[walletType]?.[BETSLIP_TICKET_TYPE.PRE_MATCH]?.maxTicketStake ??
        Infinity;
      stakeLimit = calculateMaxStakeAmountForNormalBettingSlip(combinationsNumber, maxStake);
      break;
    default:
      stakeLimit = 0;
      break;
  }
  return stakeLimit;
};

export const prepareStakeMessages = (
  betslipTicketType: BetslipTicketType,
  walletType: WalletType,
  globalTicketConditions?: GlobalTicketConditionsResponse,
  errorType?: StakeErrorType
) => {
  switch (errorType) {
    case 'min':
      if (globalTicketConditions?.[walletType]?.[betslipTicketType]?.minTicketStake) {
        return {
          type: 'errors:validation.minStake',
          amount: globalTicketConditions[walletType][betslipTicketType].minTicketStake,
        };
      }
      break;
    case 'minSingleInPlay':
      if (globalTicketConditions?.[walletType]?.[BETSLIP_TICKET_TYPE.IN_PLAY]?.minTicketStake) {
        return {
          type: 'errors:validation.minInPlaySingleStake',
          amount: globalTicketConditions[walletType][BETSLIP_TICKET_TYPE.IN_PLAY].minTicketStake,
        };
      }
      break;
    case 'minSinglePreMatch':
      if (globalTicketConditions?.[walletType]?.[BETSLIP_TICKET_TYPE.PRE_MATCH]?.minTicketStake) {
        return {
          type: 'errors:validation.minPreMatchSingleStake',
          amount: globalTicketConditions[walletType][BETSLIP_TICKET_TYPE.PRE_MATCH].minTicketStake,
        };
      }
      break;
    case 'max':
      if (globalTicketConditions?.[walletType]?.[betslipTicketType]?.maxTicketStake) {
        return {
          type: 'errors:validation.maxStake',
          amount: globalTicketConditions[walletType][betslipTicketType].maxTicketStake,
        };
      }
      break;
    case 'maxSingleInPlay':
      if (globalTicketConditions?.[walletType]?.[BETSLIP_TICKET_TYPE.IN_PLAY]?.maxStakeSingle) {
        return {
          type: 'errors:validation.maxInPlaySingleStake',
          amount: globalTicketConditions[walletType][BETSLIP_TICKET_TYPE.IN_PLAY].maxStakeSingle,
        };
      }
      break;
    case 'maxSinglePreMatch':
      if (globalTicketConditions?.[walletType]?.[BETSLIP_TICKET_TYPE.PRE_MATCH]?.maxStakeSingle) {
        return {
          type: 'errors:validation.maxPreMatchSingleStake',
          amount: globalTicketConditions[walletType][BETSLIP_TICKET_TYPE.PRE_MATCH].maxStakeSingle,
        };
      }
      break;
    case 'maxWinLimit':
      if (globalTicketConditions?.[walletType]?.[betslipTicketType]?.maxWinning) {
        return {
          type: 'errors:validation.maxWinAmount',
          amount: globalTicketConditions[walletType][betslipTicketType].maxWinning,
        };
      }
      break;
    default:
      break;
  }
};

export const getErrorMessageType = (
  isSingle: boolean,
  ticketType: BetslipTicketType,
  prefix: 'min' | 'max'
): StakeErrorType => {
  const singleTicketTypeMap: Record<BetslipTicketType, string> = {
    [BETSLIP_TICKET_TYPE.IN_PLAY]: 'SingleInPlay',
    [BETSLIP_TICKET_TYPE.PRE_MATCH]: 'SinglePreMatch',
    [BETSLIP_TICKET_TYPE.MIXED]: '',
  };

  const suffix = isSingle ? singleTicketTypeMap[ticketType] : '';
  const errorMessageType = `${prefix}${suffix}` as StakeErrorType;

  return errorMessageType;
};

export const formatTennisScore = (score: number | null) => {
  return score === 50 ? 'A' : score;
};

export const areMarketsCombinable = (market1: MarketType, market2: MarketType) => {
  return market1.marketTypeCombiningIds?.includes(market2.id) && market2.marketTypeCombiningIds?.includes(market1.id);
};

/**
 * Checks if a bet can be combined with other bets with the same participant.
 *
 * Prevent combining markets (eg. Player 1 - Over 12.5 and Player 1 - Over 14.5) for the same participant,
 * but allow combining for different participants (eg. Player 1 - Over 12.5 and Player 2 - Over 14.5)
 *
 * @param currentBetModelIds - An array of modelIds (participant ids) of special values of the current bet.
 * @param bets - An array of bets to check against.
 * @param getSpecialValues - A function to extract special values from each bet.
 * @returns true if the bet can be combined, false otherwise.
 */
const canCombineWithoutSameParticipantConflict = <T>(
  currentBetModelIds: string[],
  bets: T[],
  getSpecialValues: (bet: T) => SpecialValue[]
) => {
  const sameMarketTypeBetsModelIds = bets.flatMap((bet) =>
    getSpecialValues(bet)
      .flatMap((specialValue) => specialValue.modelId)
      .filter(isTruthy)
  );

  return !currentBetModelIds.some((modelId) => sameMarketTypeBetsModelIds.includes(modelId));
};

/**
 * Checks if a bet is a ways bet by analyzing the market type and special values of other bets in the same event.
 *
 * A ways bet is a bet that can't be combined with other bets from the same event.
 * A "ways" conflict occurs when two bets from the same event have the same market type and special value model id, but aren't combinable.
 *
 * @param currentBet the bet to check
 * @param bets the array of bets to compare with
 * @param getEventId a function that returns the event id of a bet
 * @param getMarketType a function that returns the market type of a bet
 * @param getSpecialValues a function that returns the special values of a bet
 * @returns true if the bet is a ways bet, false otherwise
 */
const checkIsWaysEvent = <TBetEvent>(
  currentBet: TBetEvent,
  bets: TBetEvent[],
  getEventId: (bet: TBetEvent) => string,
  getMarketType: (bet: TBetEvent) => MarketType,
  getSpecialValues: (bet: TBetEvent) => SpecialValue[]
) => {
  const sameEventBets = bets.filter((bet) => getEventId(bet) === getEventId(currentBet));
  if (sameEventBets.length === 1) return false;

  // If we find another bet from the same event that can't be combined with the current bet, mark it as a ways bet
  const canCombineMarkets = sameEventBets.some(
    (bet) => bet !== currentBet && areMarketsCombinable(getMarketType(currentBet), getMarketType(bet))
  );

  // No need to check special values if we can't combine the markets
  if (!canCombineMarkets) return true;

  const sameMarketTypeBets = sameEventBets.filter(
    (bet) => bet !== currentBet && getMarketType(bet).id === getMarketType(currentBet).id
  );

  const currentBetModelIds = getSpecialValues(currentBet)
    .map((specialValue) => specialValue.modelId)
    .filter(isTruthy);

  return !canCombineWithoutSameParticipantConflict(currentBetModelIds, sameMarketTypeBets, getSpecialValues);
};

export const checkIsWaysEventForBetslip = (currentBet: BetslipEvent, bets: BetslipEvent[]) => {
  return checkIsWaysEvent(
    currentBet,
    bets,
    (bet) => bet.eventId,
    (bet) => bet.marketType,
    (bet) => bet.specialValues ?? []
  );
};

export const checkIsWaysEventForReofferedBetslip = (
  currentBet: BetWithMarketOutcomeEvent,
  bets: BetWithMarketOutcomeEvent[]
) => {
  return checkIsWaysEvent(
    currentBet,
    bets,
    (bet) => bet.event.id,
    (bet) => bet.market.marketType,
    (bet) => bet.outcome.specialValues ?? []
  );
};

export const isWaysBetslip = (betslip: BetslipEvent[]) => {
  return betslip.some((bet) => checkIsWaysEventForBetslip(bet, betslip));
};

export const getOutcomesByOutcomeTypeWithLength = (outcomeTypes: OutcomeType[], outcomes: Outcome[]) => {
  return outcomeTypes.reduce(
    (acc: { outcomesByType: { [id: string]: (Outcome | null)[] }; totalOutcomesLength: number }, outcomeType) => {
      const outcomesForType = outcomes.filter((outcome) => outcome.outcomeType?.id === outcomeType.id);

      // If a type has no available outcomes, make a dummy array so we know where to render an empty outcome cell
      acc.outcomesByType[outcomeType.id] = outcomesForType.length > 0 ? outcomesForType : [null];
      // Count the total number of outcomes (empty included) so we can get the appropriate widths
      acc.totalOutcomesLength += acc.outcomesByType[outcomeType.id].length;

      return acc;
    },
    { outcomesByType: {}, totalOutcomesLength: 0 }
  );
};

export const getOutcomesByParticipantsWithLength = (participants: PublishedEventParticipants, outcomes: Outcome[]) => {
  const getSortedOutcomes = (teamName: string) => {
    return outcomes.filter((o) => o.team?.name === teamName).sort((a, b) => parseFloat(a.odds) - parseFloat(b.odds));
  };

  const outcomesByParticipant = {
    [participants.home.name]: getSortedOutcomes(participants.home.name),
    [participants.away.name]: getSortedOutcomes(participants.away.name),
  };

  return {
    outcomesByParticipant,
    outcomesLength: Math.max(
      outcomesByParticipant[participants.home.name].length,
      outcomesByParticipant[participants.away.name].length
    ),
  };
};

export const getLocalizedBettingType = (t: TFunction, bettingType: BettingType): string => {
  const map = getLocalizedBettingTypeMap(t);
  return map[bettingType] || bettingType;
};

export const getLocalizedBettingTypeFromHitCount = (t: TFunction, requiredHitCount: number): string => {
  const bettingType = Object.keys(BETTING_TYPE_HIT_COUNT_MAP)[requiredHitCount - 1] as BettingType;
  return getLocalizedBettingType(t, bettingType);
};

export const formatTicketId = (id: string) => id.replace(/^0+/, '');
